import { Typography } from "antd";
import { useInsightMenuItems } from "./InsightCardMenuItems";
import { InsightProps } from "./insights";
import InsightAvatar from "./InsightAvatar";
import { formattedDate } from "@/utils/dateUtils";
import { capitalizeFirst } from "@/utils/textFormattingUtils";
import MenuDropdown from "./MenuDropdown";
import { isInsightArchived } from "./utils";
import CadenceIcon from "@/assets/icons/CadenceIcon.svg?react";
import SharedIcon from "@/assets/icons/SharedIcon.svg?react";
import MoreOptionsIcon from "@/assets/icons/MoreOptionsIcon.svg?react";

const { Text } = Typography;

type ExpandedInsightProps = {
  insight: InsightProps;
  onOpenDrawer: (insight: InsightProps) => void;
  onOpenEditModal: () => void;
  onArchiveInsight: () => void;
  onOpenDeleteModal: () => void;
  onOpenDeleteAlertModal: () => void;
  onActivateInsight: () => void;
  onOpenAddAlertModal: () => void;
  userName?: string;
  onOpenShareModal?: () => void;
};

export default function ExpandedInsightCard({
  insight,
  onOpenDrawer,
  onOpenEditModal,
  onArchiveInsight,
  onOpenDeleteModal,
  onOpenDeleteAlertModal,
  onActivateInsight,
  onOpenAddAlertModal,
  userName,
  onOpenShareModal,
}: ExpandedInsightProps) {
  const items = useInsightMenuItems({
    insight,
    onOpenEditModal,
    onArchiveInsight,
    onOpenDeleteModal,
    onOpenDeleteAlertModal,
    onActivateInsight,
    onOpenAddAlertModal,
    onOpenShareModal,
  });

  if (!insight) return null;

  return (
    <div
      data-testid="compact-insight-card"
      className="flex flex-col bg-white px-3 pt-2 pb-3 rounded-lg gap-2 hover:shadow-md transition-shadow duration-200 cursor-pointer"
      onClick={() => {
        onOpenDrawer(insight);
      }}
    >
      <span data-testid={`compact-insight-card-${insight.id}`} />

      <div className="flex justify-between items-center gap-3">
        <div className="flex items-center gap-3 shrink-0">
          <p className="text-[#00000073] text-sm whitespace-nowrap">
            {formattedDate(insight?.insight_result_timestamp, "en-US")}
          </p>
          {!isInsightArchived(insight) && (
            <div className="flex items-center gap-1">
              <CadenceIcon className="w-4 h-4" />
              <p className="text-[#00000073] text-sm whitespace-nowrap">
                {capitalizeFirst(insight.cadence)}
              </p>
            </div>
          )}
          {insight.is_shared && (
            <div className="flex items-center gap-1 text-[#00000073]">
              <SharedIcon className="w-4 h-4" />
              <p className="text-sm whitespace-nowrap">Shared</p>
            </div>
          )}
        </div>
        <MenuDropdown
          items={Array.isArray(items) ? items : []}
          icon={<MoreOptionsIcon />}
        />
      </div>

      <div className="flex items-start gap-3 min-w-0">
        <div className="shrink-0">
          <InsightAvatar insight={insight} userName={userName} />
        </div>
        <Text
          className="text-[#2B4752] text-base font-normal"
          style={{ margin: 0 }}
        >
          {capitalizeFirst(insight.title)}
        </Text>
      </div>
    </div>
  );
}
